import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface ShrineProps {
  position: [number, number, number];
  scale?: number;
  type?: 'fire' | 'earth' | 'water' | 'wind';
}

export const Shrine: React.FC<ShrineProps> = ({
  position,
  scale = 1,
  type = 'fire',
}) => {
  // Color schemes for different shrine types
  const colors = useMemo(() => {
    switch (type) {
      case 'fire':
        return {
          base: '#795548', // Brown
          pillar: '#A1887F', // Light brown
          ornament: '#F44336', // Red
          light: '#FFEB3B', // Yellow
        };
      case 'earth':
        return {
          base: '#795548', // Brown
          pillar: '#8D6E63', // Medium brown
          ornament: '#4CAF50', // Green
          light: '#8BC34A', // Light green
        };
      case 'water':
        return {
          base: '#607D8B', // Blue gray
          pillar: '#90A4AE', // Light blue gray
          ornament: '#2196F3', // Blue
          light: '#03A9F4', // Light blue
        };
      case 'wind':
        return {
          base: '#9E9E9E', // Gray
          pillar: '#BDBDBD', // Light gray
          ornament: '#00BCD4', // Cyan
          light: '#B3E5FC', // Very light blue
        };
      default:
        return {
          base: '#795548',
          pillar: '#A1887F',
          ornament: '#F44336',
          light: '#FFEB3B',
        };
    }
  }, [type]);

  // Position shrine's base at the ground level
  const adjustedPosition: [number, number, number] = [
    position[0],
    position[1] + 0.5 * scale, // Account for base height
    position[2],
  ];

  // Use refs for animation instead of state
  const timeRef = useRef(0);
  const ornamentRef = useRef<THREE.Mesh>(null);
  const lightRef = useRef<THREE.PointLight>(null);

  // Use useFrame for animation
  useFrame((_, delta) => {
    timeRef.current += delta * 2;

    // Calculate light intensity with gentle pulsing
    const intensity = 0.3 + Math.sin(timeRef.current) * 0.2;

    // Update material and light directly
    if (ornamentRef.current && ornamentRef.current.material) {
      const material = ornamentRef.current
        .material as THREE.MeshStandardMaterial;
      material.emissiveIntensity = intensity;
    }

    if (lightRef.current) {
      lightRef.current.intensity = 0.8 + Math.sin(timeRef.current) * 0.2;
    }
  });

  // Random rotation for variety
  const rotation = useMemo(() => {
    return [0, Math.random() * Math.PI * 2, 0];
  }, []);

  return (
    <group position={adjustedPosition} scale={scale} rotation={rotation as any}>
      {/* Base */}
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 0.5, 2]} />
        <meshStandardMaterial color={colors.base} roughness={0.8} />
      </mesh>

      {/* Steps */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.6, 0.1, 1.6]} />
        <meshStandardMaterial color={colors.base} roughness={0.7} />
      </mesh>

      {/* Pillars */}
      <mesh position={[-0.6, 1, -0.6]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 2, 6]} />
        <meshStandardMaterial color={colors.pillar} roughness={0.5} />
      </mesh>
      <mesh position={[0.6, 1, -0.6]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 2, 6]} />
        <meshStandardMaterial color={colors.pillar} roughness={0.5} />
      </mesh>
      <mesh position={[-0.6, 1, 0.6]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 2, 6]} />
        <meshStandardMaterial color={colors.pillar} roughness={0.5} />
      </mesh>
      <mesh position={[0.6, 1, 0.6]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 2, 6]} />
        <meshStandardMaterial color={colors.pillar} roughness={0.5} />
      </mesh>

      {/* Roof */}
      <mesh position={[0, 2.1, 0]} castShadow>
        <boxGeometry args={[1.8, 0.2, 1.8]} />
        <meshStandardMaterial color={colors.base} roughness={0.6} />
      </mesh>
      <mesh position={[0, 2.3, 0]} castShadow>
        <coneGeometry args={[1.2, 0.8, 4]} />
        <meshStandardMaterial color={colors.pillar} roughness={0.6} />
      </mesh>

      {/* Central ornament */}
      <mesh ref={ornamentRef} position={[0, 0.9, 0]} castShadow>
        <sphereGeometry args={[0.3, 8, 8]} />
        <meshStandardMaterial
          color={colors.ornament}
          emissive={colors.light}
          emissiveIntensity={0.3}
          roughness={0.2}
        />
      </mesh>

      {/* Simple light source */}
      <pointLight
        ref={lightRef}
        position={[0, 1, 0]}
        intensity={0.8}
        distance={5}
        color={colors.light}
        castShadow={false}
      />
    </group>
  );
};
